import React, { useEffect, useRef, useState } from 'react';
import { GameInterface } from '../constants/GameInterface';
import GameHeader from '../GameHeader';
import WaitingRoomOnePlayer from './WaitingRoomOnePlayer';
import WaitingRoomEightPlayers from './WaitingRoomEightPlayers';
import WaitingRoomSevenPlayers from './WaitingRoomSevenPlayers';
import WaitingRoomSixPlayers from './WaitingRoomSixPlayers';
import WaitingRoomFivePlayers from './WaitingRoomFivePlayers';
import WaitingRoomFourPlayers from './WaitingRoomFourPlayers';
import WaitingRoomThreePlayers from './WaitingRoomThreePlayers';
import WaitingRoomTwoPlayers from './WaitingRoomTwoPlayers';
import RulesDialog from '../dialogs/RulesDialog';
import RemovePlayerDialog from '../dialogs/RemovePlayerDialog';
import EndExitGameDialog from '../dialogs/EndExitGameDialog';
import EndExitGameNotificationDialog from '../dialogs/EndExitGameNotificationDialog';
import { Dialogs } from '../constants/Dialogs';
import { supabase } from '../supabase/supabase';
import { Game } from '../classes/Game';
import { Popups } from '../constants/Popups';
import { END_EXIT_GAME_NOTIFICATION_DIALOG_DURATION_IN_SECONDS, MAX_NUM_OF_PLAYERS, MIN_NUM_OF_PLAYERS } from '../constants/Constants';
import LoadingComponent from '../commonComponents/LoadingComponent';
import { Player } from '../classes/Player';
import { arrangePlayers } from '../utils/ArrangePlayers';
import { PronounForm } from '../constants/PronounForms';
import { getPronoun } from '../utils/PronounsUtil';
import { getActivePlayersCount } from '../utils/GetActivePlayersCount';
import { Stats } from '../classes/Stats';
import { RoundPhase } from '../constants/RoundPhase';
import HeaderInGame from '../HeaderInGame';
import BasicInfoDialog from '../dialogs/BasicInfoDialog';
import { PageView } from '../classes/PageView';
import { Pages } from '../constants/Pages';
import { useNavigate } from "react-router-dom";

function WaitingRoomDialog({dialog, setDialog, isHost, setIsHost, gameId, setGameId, playerId, setPlayerId, isLate, setIsLate, statsId, setStatsId}: GameInterface) {
    const navigate = useNavigate();
    const [players, setPlayers] = useState<Player[]>([]);
    const [isDeletingGame, setIsDeletingGame] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [popup, setPopup] = useState(Popups.none);
    const [endExitGameNotificationTitle, setEndExitGameNotificationTitle] = useState("");
    const [endExitGameNotificationSubtitle, setEndExitGameNotificationSubtitle] = useState("");

    const playersRef = useRef<Player[]>([])

    const getPlayers = async () => {
        setPlayers([]);
        let tempPlayers = await Player.getActivePlayers(gameId);
        tempPlayers = arrangePlayers(tempPlayers, playerId);
        const tempPlayers2 = [];
        for (let idx = 0; idx < tempPlayers.length; idx++)
        {
            tempPlayers2.push(tempPlayers[idx]);
        }
        setPlayers([...tempPlayers2]);
        return tempPlayers2;
    }

    const handlePlayersTableChanges = async (payload: any) => {
        const playerChangeJson = payload.new;
        const player = Player.fromJson(playerChangeJson);
        const idx = playersRef.current.findIndex((p) => p.id === player.id);
        if (idx === -1) {
            // new player joined
            let tempPlayers = [...playersRef.current];
            tempPlayers.push(player);
            tempPlayers = arrangePlayers(tempPlayers, playerId);
            const tempPlayers2 = [];
            for (let idx = 0; idx < tempPlayers.length; idx++)
            {
                tempPlayers2.push(tempPlayers[idx]);
            }
            setPlayers([...tempPlayers2]);
        } else {
            // player exited the game
            if (player.id === playerId) {
                const tempPlayers = [...playersRef.current];
                tempPlayers.splice(idx, 1);
                setPlayers([...tempPlayers]);
                navigate("/");
            } else {
                const title = player.status === 2 ? playersRef.current[idx].name + " left the game" : playersRef.current[idx].name + " is kicked out";
                const subtitle = player.status === 2 ? "Guess " + getPronoun(playersRef.current[idx].gender, PronounForm.they) + " couldn't take the heat" : "#awkward";
                setEndExitGameNotificationTitle(title);
                setEndExitGameNotificationSubtitle(subtitle);
                setPopup(Popups.endExitGameNotification);
                const tempPlayers = [...playersRef.current];
                tempPlayers.splice(idx, 1);
                setPlayers([...tempPlayers]);
            }
        }
    }

    const handleGamesTableChanges = async (payload: any) => {
        const gameChangeJson = payload.new;
        const game = Game.fromJson(gameChangeJson);
        if (game.status === 1) {
            // game has started
            setIsLate(false);
            setDialog(Dialogs.gameRoom);
        } else if (game.status === 2) {
            // game has ended
            if (isHost) {
                setIsDeletingGame(true);
                await Game.deleteGame(gameId);
                setIsDeletingGame(false);
                navigate("/");
            } else {
                setEndExitGameNotificationTitle("Game Ended");
                setEndExitGameNotificationSubtitle("The host ended the game. See you next time!");
                setPopup(Popups.endExitGameNotification);
                setTimeout(() => {
                    navigate("/");
                }, END_EXIT_GAME_NOTIFICATION_DIALOG_DURATION_IN_SECONDS * 1000)
            }
        }
    }

    const handleClickReady = async () => {
        if (isHost && players.length >= MIN_NUM_OF_PLAYERS && players.length <= MAX_NUM_OF_PLAYERS) {
            const count = getActivePlayersCount(players);
            if (count >= MIN_NUM_OF_PLAYERS) {
                // start game
                await Game.updateGameStatus(gameId, 1);
            }
        }
    }

    const openEndExitGameDialog = () => {
        setPopup(Popups.endExitGame);
    }

    const openRulesDialog = () => {
        Stats.updateRulesClickCount(statsId);
        setPopup(Popups.rules);
    }

    const handleRefresh = async () => {
        if (!isRefreshing) {
            setIsRefreshing(true);
            await refresh();
            setIsRefreshing(false);
        }
    }

    const openRemovePlayerDialog = () => {
        setPopup(Popups.removePlayer);
    }

    const closeDialog = () => {
        setPopup(Popups.none);
    }

    const handleRemovePlayer = async (idx: number) => {
        if (idx < players.length) {
            await Player.updatePlayerStatus(players[idx].id, 3);
            Stats.updateDropoffCount(statsId);
            setPopup(Popups.none);
        }
    }

    const refresh = async () => {
        const games = await Game.getGames(gameId);
        if (games.length === 0 || games[0].status === 2 || games[0].status === 3) {
            // Game ended - go home
            navigate("/");
        } else if (games[0].status === 0 || games[0].status === 1) {
            // still in the waiting room or moved to game room
            const refreshPlayers = await getPlayers();
            const idx = refreshPlayers.findIndex((p) => p.id === playerId);
            if (idx === -1) {
                // the player was removed from the game - go home
                navigate("/");
            } else {
                if (games[0].status === 1)
                {
                    setIsLate(true);
                    setDialog(Dialogs.gameRoom);
                }
            }
        }
    }

    useEffect(() => {
          playersRef.current = [...players];
    }, [players])

    useEffect(() => {
        PageView.addNewPageView(Pages.WaitingRoom);
    }, [])

    const handleVisibilityChange = async () => {
        if (document.hidden) {
            supabase.removeAllChannels();
        } else {
            subscribeGamesChannel();
            subscribePlayersChannel();
            refresh();
        }
    }

    const subscribeGamesChannel = () => {
        supabase
        .channel('gamesWaitingRoomChannel')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'Games', filter: `id=eq.${gameId}`, }, handleGamesTableChanges)
        .subscribe();
    }

    const subscribePlayersChannel = () => {
        supabase
        .channel('playersWaitingRoomChannel')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'Players', filter: `game=eq.${gameId}`, }, handlePlayersTableChanges)
        .subscribe();
    }

    useEffect(() => {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        getPlayers();
        subscribePlayersChannel();
        subscribeGamesChannel()

        setTimeout(() => {
            setPopup(Popups.basicInfo);
        }, 1000)
        
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            supabase.removeAllChannels();
        }
    }, [])

    return (
        <div className="overallWrapper">
            <HeaderInGame/>
            <div className="waitingRoomWrapper">
                <div className="waitingRoomTop">
                    <GameHeader players={players} isHost={isHost} roundPhase={RoundPhase.initialPhase} popup={popup} openEndExitGameDialog={openEndExitGameDialog} openRulesDialog={openRulesDialog} refresh={handleRefresh} openRemovePlayerDialog={openRemovePlayerDialog}/>
                    <div className="waitingRoomGameCode">
                        <div className="waitingRoomCode">{gameId}</div>
                    </div>
                </div>
                <div className="waitingRoomMiddle">
                    {players.length === 1 && <WaitingRoomOnePlayer players={players} isHost={isHost} />}
                    {players.length === 2 && <WaitingRoomTwoPlayers players={players} />}
                    {players.length === 3 && <WaitingRoomThreePlayers players={players} />}
                    {players.length === 4 && <WaitingRoomFourPlayers players={players} />}
                    {players.length === 5 && <WaitingRoomFivePlayers players={players} />}
                    {players.length === 6 && <WaitingRoomSixPlayers players={players} />}
                    {players.length === 7 && <WaitingRoomSevenPlayers players={players} />}
                    {players.length === 8 && <WaitingRoomEightPlayers players={players} />}
                </div>
                <div className="waitingRoomBottom">
                    {isHost ?
                    <>
                        <button className="waitingRoomStartGameButton" onClick={handleClickReady}>START GAME</button>
                        <div className="waitingRoomInfo">*Requires at least 4 players to start.</div>
                    </> :
                    <div className="waitingRoomInfo">Waiting for the host to start the game...</div>
                    }
                </div>
                {popup === Popups.basicInfo && <BasicInfoDialog closeDialog={closeDialog}/>}
                {popup === Popups.rules && <RulesDialog closeDialog={closeDialog}/>}
                {popup === Popups.removePlayer && <RemovePlayerDialog players={players} playerId={playerId} closeDialog={closeDialog} handleRemovePlayer={handleRemovePlayer}/>}
                {popup === Popups.endExitGame && <EndExitGameDialog gameId={gameId} playerId={playerId} isHost={isHost} statsId={statsId} closeDialog={closeDialog}/>}
                {popup === Popups.endExitGameNotification && <EndExitGameNotificationDialog title={endExitGameNotificationTitle} subtitle={endExitGameNotificationSubtitle} closeDialog={closeDialog}/>}
                {isDeletingGame && <LoadingComponent/>}
            </div>
        </div>
    );
};

export default WaitingRoomDialog;